import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Panel } from "@/components/ui/Panel";

import { cn } from "@/lib/utils";
import { roomFor, sideIn } from "./room";
import { toneClasses } from "./tone";

/** The space between a trigger and its tip, as the larger of the two tips' margins draws it. */
const GAP = 8;

/**
 * How wide a dark tip is: narrow hugs its words, for a label naming the control under it;
 * wide is the fixed column a short read wants.
 */
const DARK_WIDTH = {
  wide: "w-[220px] whitespace-normal",
  narrow: "w-max max-w-[220px] whitespace-nowrap",
} as const;

/**
 * Where a light tip sits against its trigger: on its preferred side, or, where `useTipSide`
 * found no room there, on the other.
 */
const PLACED = {
  above:
    "bottom-full mb-2 data-[side=below]:top-full data-[side=below]:bottom-auto data-[side=below]:mt-2 data-[side=below]:mb-0",
  below:
    "top-full mt-2 data-[side=above]:top-auto data-[side=above]:bottom-full data-[side=above]:mt-0 data-[side=above]:mb-2",
} as const;

/** What a trigger spreads to take part: the tip's id for readers, and the rests it watches. */
export interface HoverTipTriggerProps {
  "aria-describedby": string | undefined;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocus: () => void;
  onBlur: () => void;
}

/**
 * Open the tip on the side it prefers unless the box that clips it, or the window, has no room
 * there and has room on the other: a tip over the first claim in a scrolling list of claims
 * would be cut off above it. Set on the element before paint, as the side is a fact of the
 * layout and not of what the tip says.
 */
function useTipSide(
  open: boolean,
  tone: "light" | "dark",
  placement: "above" | "below",
) {
  const prefer = tone === "dark" ? "above" : placement;
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const tip = tipRef.current;
    const anchor = anchorRef.current;
    if (!open || tip === null || anchor === null) return;
    delete tip.dataset.side;
    const { side } = sideIn(
      roomFor(anchor),
      anchor.getBoundingClientRect(),
      tip.getBoundingClientRect().height,
      GAP,
      prefer,
    );
    tip.dataset.side = side;
  }, [open, prefer]);
  return { anchorRef, tipRef };
}

/** What a tip shows and how, and the trigger it is drawn beside. */
export interface HoverTipProps {
  tip: ReactNode;
  testId?: string;
  placement?: "above" | "below";
  /** `end` for a trigger at the right edge of the page, so the tip opens leftwards and stays in the window. */
  align?: "start" | "end";
  /** Wide for a prompt's paragraphs; narrow for a phrase's few lines. */
  wide?: boolean;
  /**
   * Light for an explanation, a card of its own. Dark for a short read about
   * the thing under it, drawn above it as the design draws a claim's basis.
   */
  tone?: "light" | "dark";
  children: (tools: {
    triggerProps: HoverTipTriggerProps;
    open: boolean;
    togglePin: () => void;
  }) => ReactNode;
}

/**
 * A tip that shows while the pointer or focus rests on its trigger, and stays
 * while pinned. The one mechanism behind every hover explanation on a page, so
 * a prompt beside a heading and a markup over a phrase open and close alike.
 *
 * The trigger is the caller's: a labelled pill, a phrase, whatever the tip
 * belongs to. `togglePin` is for a trigger whose click has nothing else to do;
 * a pin lapses when the trigger loses focus.
 *
 * It opens on its side of the trigger unless the box that clips it has no room there.
 */
export function HoverTip({
  tip,
  testId,
  placement = "below",
  align = "start",
  wide = true,
  tone = "light",
  children,
}: HoverTipProps) {
  const [hovering, setHovering] = useState(false);
  const [pinned, setPinned] = useState(false);
  const id = useId();
  const open = hovering || pinned;
  const { anchorRef, tipRef } = useTipSide(open, tone, placement);
  const triggerProps: HoverTipTriggerProps = {
    "aria-describedby": open ? id : undefined,
    onMouseEnter: () => setHovering(true),
    onMouseLeave: () => setHovering(false),
    onFocus: () => setHovering(true),
    onBlur: () => {
      setHovering(false);
      setPinned(false);
    },
  };
  return (
    <span ref={anchorRef} className="relative inline">
      {children({
        triggerProps,
        open,
        togglePin: () => setPinned((p) => !p),
      })}
      {open && tone === "dark" && (
        <span
          ref={tipRef}
          id={id}
          role="tooltip"
          data-testid={testId}
          className={cn(
            "absolute bottom-full left-0 z-40 mb-[7px] data-[side=below]:top-full data-[side=below]:bottom-auto data-[side=below]:mt-[7px] data-[side=below]:mb-0 flex flex-col gap-1.5 rounded-[9px] bg-tip px-2.5 py-[9px] text-left font-normal text-white shadow-tip",
            DARK_WIDTH[wide ? "wide" : "narrow"],
          )}
        >
          {tip}
        </span>
      )}
      {open && tone === "light" && (
        <Panel
          as="span"
          shape="row"
          pad="none"
          ref={tipRef}
          id={id}
          role="tooltip"
          data-testid={testId}
          className={cn(
            "absolute z-30 flex flex-col text-left font-normal text-muted-foreground shadow-[0_8px_30px_rgba(62,1,111,0.14)]",
            align === "end" ? "right-0" : "left-0",
            PLACED[placement],
            wide
              ? "w-[min(560px,80vw)] gap-2 px-3.5 py-3 text-[12.5px] leading-relaxed"
              : "w-[320px] max-w-[80vw] gap-1 px-3 py-2.5 text-[11.5px] leading-relaxed",
          )}
        >
          {tip}
        </Panel>
      )}
    </span>
  );
}

/**
 * The small chip a tip opens from: an icon and a word or two, the one trigger
 * every tip on a page is drawn as. `brand` beside a heading, for an explanation;
 * `quiet` in a row of acts, for a short read about the item; `key` for the
 * larger blue Key v0.28 draws at a Thesis list's heading.
 */
const CHIP = {
  brand:
    "border-primary/15 bg-primary/[0.06] px-2.5 py-0.5 text-[11.5px] font-semibold text-primary hover:bg-primary/[0.1] [&_svg]:size-3",
  quiet:
    "h-5 border-border bg-card px-2 text-[10px] font-bold tracking-[0.02em] text-muted-foreground hover:text-brand-mid [&_svg]:size-[11px]",
  key: `${toneClasses("info", "surface")} gap-[7px] px-3 py-[5px] text-[14px] font-semibold hover:brightness-95 [&_svg]:size-[17px]`,
} as const;

export function TipChip({
  label,
  icon,
  tip,
  tone = "light",
  chip = "brand",
  align = "start",
  testId,
}: {
  label: string;
  icon: ReactNode;
  tip: ReactNode;
  tone?: "light" | "dark";
  chip?: keyof typeof CHIP;
  /** `end` for a chip at the right of its row, as a heading's key is. */
  align?: "start" | "end";
  testId?: string;
}) {
  return (
    <HoverTip tip={tip} tone={tone} align={align} testId={testId}>
      {({ triggerProps, open, togglePin }) => (
        <button
          type="button"
          aria-expanded={open}
          {...triggerProps}
          onClick={togglePin}
          className={cn(
            "inline-flex flex-none cursor-help items-center gap-1 rounded-full border whitespace-nowrap focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none",
            CHIP[chip],
          )}
        >
          {icon}
          {label}
        </button>
      )}
    </HoverTip>
  );
}
